import {ChatNotesError} from './errors';

export const CHAT_NOTES_PROTOCOL_VERSION = 1;
export const SYSTEM_STATUS_CAPABILITY = 'system.status';
export const TELEGRAM_AUTH_CAPABILITY = 'auth.telegram';
export const DEVELOPMENT_AUTH_CAPABILITY = 'auth.development';
export const CURRENT_SESSION_CAPABILITY = 'session.current';
export const REVOKE_SESSION_CAPABILITY = 'session.revoke';
export const READ_FOLDERS_CAPABILITY = 'folders.read';
export const WRITE_FOLDERS_CAPABILITY = 'folders.write';
export const READ_NOTES_CAPABILITY = 'notes.read';
export const WRITE_NOTES_CAPABILITY = 'notes.write';
export const READ_DRAFTS_CAPABILITY = 'drafts.read';
export const WRITE_DRAFTS_CAPABILITY = 'drafts.write';
export const READ_ATTACHMENTS_CAPABILITY = 'attachments.read';
export const WRITE_ATTACHMENTS_CAPABILITY = 'attachments.write';
export const READ_NOTIFICATIONS_CAPABILITY = 'notifications.read';
export const WRITE_NOTIFICATIONS_CAPABILITY = 'notifications.write';

export interface ChatNotesCoreStatus {
  service: string;
  version: string;
  apiVersion: string;
  environment: string;
  integration: {
    protocolVersion: number;
    capabilities: string[];
  };
  database: {
    status: 'ready';
    schemaVersion: string;
  };
}

export interface ChatNotesSuccessEnvelope<T> {
  ok: true;
  data: T;
  meta: {
    requestId: string;
  };
}

export interface ChatNotesUser {
  id: string;
  telegramUserId: string;
  firstName: string;
  lastName?: string;
  username?: string;
  languageCode?: string;
  isPremium: boolean;
  photoUrl?: string;
}

export interface ChatNotesIssuedSession {
  token: string;
  tokenType: 'Bearer';
  expiresAt: string;
  user: ChatNotesUser;
}

export interface ChatNotesCurrentSession {
  user: ChatNotesUser;
}

export interface ChatNotesFolder {
  id: string;
  folderNumber: number;
  title: string;
  icon?: string;
  position: number;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChatNotesNote {
  id: string;
  folderId?: string;
  messageNumber: number;
  content: string;
  contentFormat: 'plain' | 'markdown';
  attachmentId?: string;
  groupId?: string;
  formatting: ChatNotesFormatting[];
  replyToNoteId?: string;
  forwardedFromNoteId?: string;
  isPinned: boolean;
  reactions: string[];
  remindAt?: string;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChatNotesFormatting {
  type: string;
  offset: number;
  length: number;
  data?: string;
}

export interface ChatNotesNotePage {
  notes: ChatNotesNote[];
  nextCursor?: string;
}

export interface ChatNotesDraft {
  folderId?: string;
  content: string;
  formatting: ChatNotesFormatting[];
  replyToNoteId?: string;
  updatedAt: string;
}

export type ChatNotesAttachmentKind =
  'photo' | 'video' | 'document' | 'audio' | 'voice' | 'video_note' | 'animation';

export interface ChatNotesAttachment {
  id: string;
  kind: ChatNotesAttachmentKind;
  fileName: string;
  mimeType: string;
  size: number;
  width?: number;
  height?: number;
  duration?: number;
  createdAt: string;
}

export interface ChatNotesNotificationSettings {
  scope: string;
  showPreviews: boolean;
  muteUntil: number;
  updatedAt: string;
}

interface UnknownRecord {
  [key: string]: unknown;
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof(value) === 'object' && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof(item) === 'string');
}

function invalidResponse(): never {
  throw new ChatNotesError('INVALID_RESPONSE', 'Chat Notes Core returned an invalid response.');
}

function parseEnvelope(value: unknown): {
  data: UnknownRecord;
  meta: {requestId: string};
} {
  if(!isRecord(value) || value.ok !== true || !isRecord(value.data) || !isRecord(value.meta)) {
    return invalidResponse();
  }
  if(typeof value.meta.requestId !== 'string') return invalidResponse();
  return {data: value.data, meta: {requestId: value.meta.requestId}};
}

function optionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof(value) === 'string';
}

function parseUser(value: unknown): ChatNotesUser {
  if(
    !isRecord(value) ||
    typeof(value.id) !== 'string' ||
    typeof(value.telegramUserId) !== 'string' ||
    typeof(value.firstName) !== 'string' ||
    !optionalString(value.lastName) ||
    !optionalString(value.username) ||
    !optionalString(value.languageCode) ||
    typeof(value.isPremium) !== 'boolean' ||
    !optionalString(value.photoUrl)
  ) {
    return invalidResponse();
  }
  return value as unknown as ChatNotesUser;
}

function parseFolder(value: unknown): ChatNotesFolder {
  if(
    !isRecord(value) ||
    typeof(value.id) !== 'string' ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.id) ||
    typeof(value.folderNumber) !== 'number' ||
    !Number.isSafeInteger(value.folderNumber) ||
    value.folderNumber < 2 ||
    typeof(value.title) !== 'string' ||
    !optionalString(value.icon) ||
    typeof(value.position) !== 'number' ||
    !Number.isSafeInteger(value.position) ||
    value.position < 1 ||
    typeof(value.revision) !== 'number' ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 1 ||
    typeof(value.createdAt) !== 'string' ||
    !Number.isFinite(Date.parse(value.createdAt)) ||
    typeof(value.updatedAt) !== 'string' ||
    !Number.isFinite(Date.parse(value.updatedAt))
  ) {
    return invalidResponse();
  }
  return value as unknown as ChatNotesFolder;
}

function parseFormatting(value: unknown): ChatNotesFormatting[] {
  if(value === undefined) return [];
  if(!Array.isArray(value) || value.length > 128) return invalidResponse();
  return value.map((item) => {
    if(
      !isRecord(item) ||
      typeof(item.type) !== 'string' ||
      typeof(item.offset) !== 'number' || !Number.isSafeInteger(item.offset) || item.offset < 0 ||
      typeof(item.length) !== 'number' || !Number.isSafeInteger(item.length) || item.length < 1 ||
      !optionalString(item.data)
    ) return invalidResponse();
    return item as unknown as ChatNotesFormatting;
  });
}

function parseReactions(value: unknown): string[] {
  if(value === undefined) return [];
  if(!isStringArray(value) || value.length > 20) return invalidResponse();
  return value;
}

function parseNote(value: unknown): ChatNotesNote {
  if(
    !isRecord(value) ||
    typeof(value.id) !== 'string' ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.id) ||
    !optionalString(value.folderId) ||
    typeof(value.messageNumber) !== 'number' ||
    !Number.isSafeInteger(value.messageNumber) ||
    value.messageNumber < 1 ||
    value.messageNumber > 0x7fffffff ||
    typeof(value.content) !== 'string' ||
    (value.contentFormat !== 'plain' && value.contentFormat !== 'markdown') ||
    !optionalString(value.attachmentId) ||
    !optionalString(value.groupId) ||
    !optionalString(value.replyToNoteId) ||
    !optionalString(value.forwardedFromNoteId) ||
    (value.isPinned !== undefined && typeof(value.isPinned) !== 'boolean') ||
    !optionalString(value.remindAt) ||
    (typeof(value.remindAt) === 'string' && !Number.isFinite(Date.parse(value.remindAt))) ||
    typeof(value.revision) !== 'number' ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 1 ||
    typeof(value.createdAt) !== 'string' ||
    !Number.isFinite(Date.parse(value.createdAt)) ||
    typeof(value.updatedAt) !== 'string' ||
    !Number.isFinite(Date.parse(value.updatedAt))
  ) {
    return invalidResponse();
  }
  return {
    ...(value as unknown as ChatNotesNote),
    formatting: parseFormatting(value.formatting),
    isPinned: value.isPinned === true,
    reactions: parseReactions(value.reactions)
  };
}

function parseDraft(value: unknown): ChatNotesDraft {
  if(
    !isRecord(value) ||
    !optionalString(value.folderId) ||
    typeof(value.content) !== 'string' ||
    !optionalString(value.replyToNoteId) ||
    typeof(value.updatedAt) !== 'string' ||
    !Number.isFinite(Date.parse(value.updatedAt))
  ) return invalidResponse();
  return {
    ...(value as unknown as ChatNotesDraft),
    formatting: parseFormatting(value.formatting)
  };
}

function parseAttachment(value: unknown): ChatNotesAttachment {
  if(
    !isRecord(value) ||
    typeof(value.id) !== 'string' ||
    !['photo', 'video', 'document', 'audio', 'voice', 'video_note', 'animation'].includes(String(value.kind)) ||
    typeof(value.fileName) !== 'string' ||
    typeof(value.mimeType) !== 'string' ||
    typeof(value.size) !== 'number' || !Number.isSafeInteger(value.size) || value.size < 1 ||
    (value.width !== undefined && (!Number.isSafeInteger(value.width) || Number(value.width) < 1)) ||
    (value.height !== undefined && (!Number.isSafeInteger(value.height) || Number(value.height) < 1)) ||
    (value.duration !== undefined && (typeof(value.duration) !== 'number' || value.duration < 0)) ||
    typeof(value.createdAt) !== 'string' || !Number.isFinite(Date.parse(value.createdAt))
  ) return invalidResponse();
  return value as unknown as ChatNotesAttachment;
}

function parseNotificationSettings(value: unknown): ChatNotesNotificationSettings {
  if(
    !isRecord(value) || typeof value.scope !== 'string' ||
    typeof value.showPreviews !== 'boolean' ||
    typeof value.muteUntil !== 'number' || !Number.isSafeInteger(value.muteUntil) || value.muteUntil < 0 ||
    typeof value.updatedAt !== 'string' || !Number.isFinite(Date.parse(value.updatedAt))
  ) return invalidResponse();
  return value as unknown as ChatNotesNotificationSettings;
}

export function parseCoreStatus(value: unknown): ChatNotesSuccessEnvelope<ChatNotesCoreStatus> {
  const {data, meta} = parseEnvelope(value);
  if(
    typeof(data.service) !== 'string' ||
    typeof(data.version) !== 'string' ||
    typeof(data.apiVersion) !== 'string' ||
    typeof(data.environment) !== 'string' ||
    !isRecord(data.integration) ||
    typeof(data.integration.protocolVersion) !== 'number' ||
    !isStringArray(data.integration.capabilities) ||
    !isRecord(data.database) ||
    data.database.status !== 'ready' ||
    typeof(data.database.schemaVersion) !== 'string'
  ) {
    return invalidResponse();
  }

  if(data.integration.protocolVersion !== CHAT_NOTES_PROTOCOL_VERSION) {
    throw new ChatNotesError(
      'INCOMPATIBLE_PROTOCOL',
      `Unsupported Chat Notes protocol version: ${data.integration.protocolVersion}.`,
      {requestId: meta.requestId}
    );
  }

  if(!data.integration.capabilities.includes(SYSTEM_STATUS_CAPABILITY)) {
    throw new ChatNotesError(
      'INCOMPATIBLE_PROTOCOL',
      `Chat Notes Core does not provide ${SYSTEM_STATUS_CAPABILITY}.`,
      {requestId: meta.requestId}
    );
  }

  return value as unknown as ChatNotesSuccessEnvelope<ChatNotesCoreStatus>;
}

export function parseIssuedSession(
  value: unknown
): ChatNotesSuccessEnvelope<ChatNotesIssuedSession> {
  const {data} = parseEnvelope(value);
  if(
    typeof(data.token) !== 'string' ||
    !/^cns_[A-Za-z0-9_-]{43}$/.test(data.token) ||
    data.tokenType !== 'Bearer' ||
    typeof(data.expiresAt) !== 'string' ||
    !Number.isFinite(Date.parse(data.expiresAt))
  ) {
    return invalidResponse();
  }
  parseUser(data.user);
  return value as ChatNotesSuccessEnvelope<ChatNotesIssuedSession>;
}

export function parseCurrentSession(
  value: unknown
): ChatNotesSuccessEnvelope<ChatNotesCurrentSession> {
  const {data} = parseEnvelope(value);
  parseUser(data.user);
  return value as ChatNotesSuccessEnvelope<ChatNotesCurrentSession>;
}

export function parseRevokedSession(value: unknown): ChatNotesSuccessEnvelope<{revoked: true}> {
  const {data} = parseEnvelope(value);
  if(data.revoked !== true) return invalidResponse();
  return value as ChatNotesSuccessEnvelope<{revoked: true}>;
}

export function parseFolders(
  value: unknown
): ChatNotesSuccessEnvelope<{folders: ChatNotesFolder[]}> {
  const {data} = parseEnvelope(value);
  if(!Array.isArray(data.folders)) return invalidResponse();
  data.folders.forEach(parseFolder);
  return value as ChatNotesSuccessEnvelope<{folders: ChatNotesFolder[]}>;
}

export function parseFolderResult(
  value: unknown
): ChatNotesSuccessEnvelope<{folder: ChatNotesFolder}> {
  const {data} = parseEnvelope(value);
  parseFolder(data.folder);
  return value as ChatNotesSuccessEnvelope<{folder: ChatNotesFolder}>;
}

export function parseDeletedFolder(value: unknown): ChatNotesSuccessEnvelope<{deleted: true}> {
  const {data} = parseEnvelope(value);
  if(data.deleted !== true) return invalidResponse();
  return value as ChatNotesSuccessEnvelope<{deleted: true}>;
}

export function parseNotes(value: unknown): ChatNotesSuccessEnvelope<ChatNotesNotePage> {
  const {data} = parseEnvelope(value);
  if(!Array.isArray(data.notes) || !optionalString(data.nextCursor)) return invalidResponse();
  return {
    ...(value as ChatNotesSuccessEnvelope<ChatNotesNotePage>),
    data: {...data, notes: data.notes.map(parseNote)} as unknown as ChatNotesNotePage
  };
}

export function parseNoteResult(
  value: unknown
): ChatNotesSuccessEnvelope<{note: ChatNotesNote}> {
  const {data} = parseEnvelope(value);
  return {
    ...(value as ChatNotesSuccessEnvelope<{note: ChatNotesNote}>),
    data: {note: parseNote(data.note)}
  };
}

export function parseDeletedNote(value: unknown): ChatNotesSuccessEnvelope<{deleted: true}> {
  const {data} = parseEnvelope(value);
  if(data.deleted !== true) return invalidResponse();
  return value as ChatNotesSuccessEnvelope<{deleted: true}>;
}

export function parseDrafts(value: unknown): ChatNotesSuccessEnvelope<{drafts: ChatNotesDraft[]}> {
  const {data} = parseEnvelope(value);
  if(!Array.isArray(data.drafts)) return invalidResponse();
  return {
    ...(value as ChatNotesSuccessEnvelope<{drafts: ChatNotesDraft[]}>),
    data: {drafts: data.drafts.map(parseDraft)}
  };
}

export function parseDraftMutation(
  value: unknown
): ChatNotesSuccessEnvelope<{saved?: true; deleted?: true}> {
  const {data} = parseEnvelope(value);
  if(data.saved !== true && data.deleted !== true) return invalidResponse();
  return value as ChatNotesSuccessEnvelope<{saved?: true; deleted?: true}>;
}

export function parseAttachmentResult(
  value: unknown
): ChatNotesSuccessEnvelope<{attachment: ChatNotesAttachment}> {
  const {data} = parseEnvelope(value);
  return {
    ...(value as ChatNotesSuccessEnvelope<{attachment: ChatNotesAttachment}>),
    data: {attachment: parseAttachment(data.attachment)}
  };
}

export function parseNotificationSettingsResult(
  value: unknown
): ChatNotesSuccessEnvelope<{settings: ChatNotesNotificationSettings}> {
  const {data} = parseEnvelope(value);
  return {
    ...(value as ChatNotesSuccessEnvelope<{settings: ChatNotesNotificationSettings}>),
    data: {settings: parseNotificationSettings(data.settings)}
  };
}
